/**
 * MOBILE HERO — SECTION 1 · top nav bar (design map §2-1 · prototype lines
 * 31–46).
 *
 * 58px bar, shared {@link MOBILE_HERO_GUTTER} side padding. Logo left,
 * Preise + Karriere viewport-centered, Anmelden top-right as white button.
 */
import { useTranslations } from "next-intl";
import Link from "next/link";
import type { CSSProperties, ReactNode } from "react";

import { BRAND_HOME_ARIA_LABEL, BRAND_LOGO_TITLE } from "@/lib/marketing-seo";
import { Logo } from "./hero-primitives";
import { H, MOBILE_HERO_GUTTER } from "./hero-tokens";
import { OPEN_POSITIONS_COUNT } from "./open-positions-count";

const TEXT_LINK: CSSProperties = {
  fontSize: 13,
  fontWeight: 500,
  color: H.ink,
  textDecoration: "none",
  whiteSpace: "nowrap",
};

const LOGIN_BTN: CSSProperties = {
  marginLeft: "auto",
  background: "#fff",
  color: H.ink,
  border: "1px solid rgba(11,15,25,0.12)",
  fontSize: 13,
  fontWeight: 600,
  padding: "8px 12px",
  borderRadius: 9,
  textDecoration: "none",
  whiteSpace: "nowrap",
};

const BADGE: CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  minWidth: 16,
  height: 16,
  padding: "0 4px",
  borderRadius: 999,
  background: H.accent,
  color: "#fff",
  fontSize: 10,
  fontWeight: 700,
  lineHeight: 1,
};

export function HeroMobileNav(): ReactNode {
  const t = useTranslations("marketing.heroMobileNav");
  return (
    <header
      style={{
        position: "sticky",
        top: 0,
        zIndex: 50,
        margin: "0 auto",
        paddingTop: "env(safe-area-inset-top, 0px)",
        background: H.page,
        borderBottom: "1px solid rgba(11,15,25,0.08)",
      }}
    >
      <div
        aria-hidden
        style={{
          position: "absolute",
          left: 0,
          right: 0,
          bottom: "100%",
          height: 200,
          background: H.page,
        }}
      />
      <div
        style={{
          position: "relative",
          height: 58,
          display: "flex",
          alignItems: "center",
          padding: `0 ${MOBILE_HERO_GUTTER}px`,
        }}
      >
        <Link
          href="/"
          aria-label={BRAND_HOME_ARIA_LABEL}
          title={BRAND_LOGO_TITLE}
          style={{ display: "inline-flex", textDecoration: "none" }}
        >
          <Logo size={24} iconOnly />
        </Link>

        <nav aria-label={t("hauptnavigation")}>
          <div
            style={{
              position: "absolute",
              left: "50%",
              top: "50%",
              transform: "translate(-50%, -50%)",
              display: "flex",
              alignItems: "center",
              gap: 14,
            }}
          >
            <Link href="/preise" style={TEXT_LINK}>
              {t("preise")}
            </Link>
            <Link
              href="/careers"
              aria-label={t("karriereOffeneStellen", {
                count: OPEN_POSITIONS_COUNT,
              })}
              style={{
                ...TEXT_LINK,
                display: "inline-flex",
                alignItems: "center",
                gap: 5,
              }}
            >
              {t("karriere")}
              <span style={BADGE} aria-hidden="true">
                {OPEN_POSITIONS_COUNT}
              </span>
            </Link>
          </div>
        </nav>

        <Link href="/login" style={LOGIN_BTN}>
          {t("anmelden")}
        </Link>
      </div>
    </header>
  );
}
